import React from 'react';
import { isObject as _isObject, forEach as _forEach } from 'lodash';

const ZOOM_LEVEL = {
  MIN: 0,
  MAX: 4,
};
const OFFSET_DEFAULT = {
  x: 0,
  y: 0,
};

export default class Img extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: false,
      onload: false,
      zoom: 0,
      offset: OFFSET_DEFAULT,
    };

    this.draggable = false;
    this.offsetRange = OFFSET_DEFAULT;
    this.clientOffset = {
      x: undefined,
      y: undefined,
    };
  }

  loadImage = src => {
    this.setState({ loading: true });

    this.src = new Image();
    this.src.src = src;
    this.src.onload = () => {
      if (!this.src) {
        return;
      }
      this.setState({ loading: false, onload: true });
    };

    this.src.onerror = () => {
      if (!this.src) {
        return;
      }
      this.setState({ loading: false, onload: false });
    };
  };

  resetOffset = () => {
    this.setState({ offset: OFFSET_DEFAULT }, () => {
      this.setOffsetRange();
    });
  };

  setOffsetRange = () => {
    const { zoom } = this.state;
    const dx = this.image.scrollWidth * (1 + zoom / 2) - this.imageOuter.clientWidth;
    const dy = this.image.scrollHeight * (1 + zoom / 2) - this.imageOuter.clientHeight;
    this.offsetRange = {
      x: Math.max(0, dx / 2),
      y: Math.max(0, dy / 2),
    };
  };

  zoomIn = () => {
    if (!this.state.onload) {
      return;
    }
    this.setState({ zoom: Math.min(this.state.zoom + 1, ZOOM_LEVEL.MAX) }, () => {
      this.setOffsetRange();
    });
  };

  zoomOut = () => {
    if (!this.state.onload) {
      return;
    }
    this.setState({ zoom: Math.max(0, this.state.zoom - 1) });
    this.resetOffset();
  };

  onMoveStart = e => {
    if (!this.offsetRange.x && !this.offsetRange.y) {
      return;
    }

    this.clientOffset = {
      x: e.clientX,
      y: e.clientY,
    };
    this.draggable = true;
  };

  onMove = e => {
    if ((!e.clientX && !e.clientY) || !this.draggable) {
      return;
    }

    const offset = {
      x: e.clientX - this.clientOffset.x,
      y: e.clientY - this.clientOffset.y,
    };

    this.clientOffset = {
      x: e.clientX,
      y: e.clientY,
    };

    this.setState({
      offset: {
        x: this.state.offset.x + offset.x,
        y: this.state.offset.y + offset.y,
      },
    });
  };

  onMoveEnd = e => {
    if (!this.mounted) {
      return;
    }

    this.draggable = false;
    const offset = {
      x: Math.abs(this.state.offset.x),
      y: Math.abs(this.state.offset.y),
    };

    let newOffset = { ...this.state.offset };
    let changed = false;

    if (Math.abs(this.state.offset.x) >= this.offsetRange.x) {
      newOffset.x = this.state.offset.x < 0 ? Math.min(0, -this.offsetRange.x) : Math.max(0, this.offsetRange.x);
      changed = true;
    }

    if (Math.abs(this.state.offset.y) >= this.offsetRange.y) {
      newOffset.y = this.state.offset.y < 0 ? Math.min(0, -this.offsetRange.y) : Math.max(0, this.offsetRange.y);
      changed = true;
    }

    this.setState({ offset: newOffset });
  };

  componentDidUpdate = (prevProps, prevState) => {
    const webLink = this.getWebLink(this.props.image);

    if (webLink != this.getWebLink(this.getWebLink(prevProps.image))) {
      this.loadImage(webLink);
      this.setState({
        zoom: 0,
      });
    }

    if (!this.state.loading && prevState.loading) {
      this.resetOffset();
    }
  };

  componentDidMount = () => {
    this.mounted = true;
    this.loadImage(this.getWebLink(this.props.image));
    window.addEventListener('resize', this.setOffsetRange);
    document.documentElement.addEventListener('mouseup', this.onMoveEnd);
  };

  componentWillUnmount = () => {
    this.mounted = false;
    if (!!this.src) {
      this.src = undefined;
    }
    window.removeEventListener('resize', this.setOffsetRange);
    document.documentElement.removeEventListener('mouseup', this.onMoveEnd);
  };

  getWebLink = file => {
    let webFile;

    if (_isObject(file))
      _forEach(file.variations || file.getVariations(), (variation, i) => {
        if (
          variation.variation_type === 'web' ||
          (variation.getVariationType && variation.getVariationType() === 'web')
        ) {
          webFile = variation.file || variation.getFile();
        }
      });

    if (webFile) {
      return webFile.web_link || webFile.getWebLink();
    }

    return file;
  };

  render() {
    const { image, index, shownIndex } = this.props;
    const { offset, zoom, loading } = this.state;

    const value = 'translate3d(' + offset.x + 'px, ' + offset.y + 'px, 0px)';
    const imageCls = 'zoom-' + zoom + ' image-outer ' + (this.draggable ? 'dragging' : '');

    const caption = (
      <p class="caption">
        {image.title ? <span class="title">{image.title}</span> : null}
        {image.title && image.content ? <span>{' - '}</span> : null}
        {image.title ? <span class="content">{image.content}</span> : null}
      </p>
    );

    return (
      <div class="image-wrapper">
        <div style={{ transform: value }} ref={component => (this.imageOuter = component)} class={imageCls}>
          {loading ? (
            <div class="spinner">
              <div class="bounce"></div>
            </div>
          ) : (
            <img
              class="image"
              ref={component => (this.image = component)}
              src={this.getWebLink(image)}
              alt={image.title || ''}
              draggable={false}
              onDragStart={e => e.preventDefault()}
              onMouseMove={this.onMove}
              onMouseDown={this.onMoveStart}
              onMouseUp={this.onMoveEnd}
            />
          )}
        </div>
        <div class="tool-bar">
          {shownIndex && <div class="index-indicator">{index}</div>}
          {caption}
          <div class="button-group">
            <div class="zoom-out button" onClick={this.zoomOut} />
            <div class="zoom-in button" onClick={this.zoomIn} />
          </div>
        </div>
      </div>
    );
  }
}
